import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  FormFeedback,
  Spinner
} from "reactstrap";
import { motion } from "framer-motion";

const EditServiceForm = ({ selectedService, handleUpdateService }) => {
  const { categories, types } = useSelector(state => state.service);
  const [isDisabled, setIsDisabled] = useState(false);
  const [formData, setFormData] = useState({
    serviceName: "",
    serviceDescription: "",
    serviceType: "",
    categoryType: "",
    servicePrice: "",
    avgExecuteTime: "",
    atHomeServiceCost: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedService) {
      setFormData({
        serviceName: selectedService.name || "",
        servicePrice: selectedService.service_price || "",
        serviceDescription: selectedService.description || "",
        serviceType: selectedService.service_type || "",
        categoryType: selectedService.service_category || "",
        atHomeServiceCost: selectedService.at_home_service_cost || "",
        avgExecuteTime: selectedService.avg_execute_time || "",
      });
    }
  }, [selectedService]);

  const handleInputChange = e => {
    const { id, value } = e.target;
    
    setFormData(prev => ({ ...prev, [id]: value }));
    
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: undefined }));
    }
  };

  const validateFields = () => {
    const newErrors = {};
    
    if (!formData.serviceName.trim()) newErrors.serviceName = "اسم الخدمة مطلوب";
    if (!formData.serviceDescription.trim()) newErrors.serviceDescription = "الوصف مطلوب";
    if (!formData.serviceType.trim()) newErrors.serviceType = "نوع الخدمة مطلوب";
    if (!formData.categoryType.trim()) newErrors.categoryType = "قسم الخدمة مطلوب";
    
    if (!formData.servicePrice || isNaN(formData.servicePrice) || formData.servicePrice < 0) 
      newErrors.servicePrice = "يجب إدخال سعر صحيح";
      
    if (!formData.avgExecuteTime || isNaN(formData.avgExecuteTime)) 
      newErrors.avgExecuteTime = "يجب إدخال وقت تنفيذ صحيح";
      
    if (formData.serviceType !== "المركز" && 
        (!formData.atHomeServiceCost || isNaN(formData.atHomeServiceCost)))
      newErrors.atHomeServiceCost = "يجب إدخال سعر صحيح للخدمة المنزلية";
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateFields();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsDisabled(true);
    setIsSubmitting(true);
    
    try {
      const updatedService = {
        id: selectedService.id,
        name: formData.serviceName,
        description: formData.serviceDescription,
        service_type: formData.serviceType,
        service_category: formData.categoryType,
        service_price: parseFloat(formData.servicePrice),
        avg_execute_time: parseInt(formData.avgExecuteTime),
        at_home_service_cost: parseFloat(formData.atHomeServiceCost || 0),
      };
      
      await handleUpdateService(updatedService);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsDisabled(false);
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Container fluid className="px-0">
        <Row>
          <Col lg="12">
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <CardTitle tag="h4" className="mb-4 text-primary">
                  <i className="fas fa-edit me-2"></i>
                  تعديل الخدمة
                </CardTitle>
                
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="serviceName" className="fw-bold">اسم الخدمة *</Label>
                        <Input
                          type="text"
                          id="serviceName"
                          value={formData.serviceName}
                          onChange={handleInputChange}
                          invalid={!!errors.serviceName}
                          className="py-2"
                          placeholder="أدخل اسم الخدمة"
                        />
                        <FormFeedback>{errors.serviceName}</FormFeedback>
                      </FormGroup>
                    </Col>
                    
                    <Col md={6}>
                      <FormGroup>
                        <Label for="servicePrice" className="fw-bold">السعر (ر.س) *</Label>
                        <Input
                          type="number"
                          id="servicePrice"
                          value={formData.servicePrice}
                          onChange={handleInputChange}
                          invalid={!!errors.servicePrice}
                          className="py-2"
                          placeholder="أدخل السعر"
                          min="0"
                          step="0.01"
                        />
                        <FormFeedback>{errors.servicePrice}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                  
                  <FormGroup>
                    <Label for="serviceDescription" className="fw-bold">الوصف *</Label>
                    <Input
                      type="textarea"
                      id="serviceDescription"
                      value={formData.serviceDescription}
                      onChange={handleInputChange}
                      invalid={!!errors.serviceDescription}
                      rows={3}
                      className="py-2"
                      placeholder="أدخل وصف الخدمة"
                    />
                    <FormFeedback>{errors.serviceDescription}</FormFeedback>
                  </FormGroup>
                  
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="serviceType" className="fw-bold">نوع الخدمة *</Label>
                        <Input
                          type="select"
                          id="serviceType"
                          value={formData.serviceType}
                          onChange={handleInputChange}
                          invalid={!!errors.serviceType}
                          className="py-2"
                        >
                          <option value="" disabled>اختر نوع الخدمة</option>
                          {types.map(type => (
                            <option key={type.id} value={type.title}>
                              {type.title}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{errors.serviceType}</FormFeedback>
                      </FormGroup>
                    </Col>
                    
                    <Col md={6}>
                      <FormGroup>
                        <Label for="categoryType" className="fw-bold">قسم الخدمة *</Label>
                        <Input
                          type="select"
                          id="categoryType"
                          value={formData.categoryType}
                          onChange={handleInputChange}
                          invalid={!!errors.categoryType}
                          className="py-2"
                        >
                          <option value="" disabled>اختر قسم الخدمة</option>
                          {Array.isArray(categories) &&
                            categories.map(category => (
                              <option key={category.id} value={category.title}>
                                {category.title}
                              </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.categoryType}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="avgExecuteTime" className="fw-bold">الوقت المتوقع (دقائق) *</Label>
                        <Input
                          type="number"
                          id="avgExecuteTime"
                          value={formData.avgExecuteTime}
                          onChange={handleInputChange}
                          invalid={!!errors.avgExecuteTime}
                          className="py-2"
                          placeholder="الوقت بالدقائق"
                          min="0"
                        />
                        <FormFeedback>{errors.avgExecuteTime}</FormFeedback>
                      </FormGroup>
                    </Col>
                    
                    {formData.serviceType !== "المركز" && (
                      <Col md={6}>
                        <FormGroup>
                          <Label for="atHomeServiceCost" className="fw-bold">تكلفة الخدمة في المنزل (ر.س)</Label>
                          <Input
                            type="number"
                            id="atHomeServiceCost"
                            value={formData.atHomeServiceCost}
                            onChange={handleInputChange}
                            invalid={!!errors.atHomeServiceCost}
                            className="py-2"
                            placeholder="أدخل السعر"
                            min="0"
                            step="0.01"
                          />
                          <FormFeedback>{errors.atHomeServiceCost}</FormFeedback>
                        </FormGroup>
                      </Col>
                    )}
                  </Row>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isDisabled}
                      className="px-4 py-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          حفظ التغييرات
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </motion.div>
  );
};

export default EditServiceForm;